import React, { useEffect, useState } from "react";
import {
  Collapse,
  Stack,
  Group,
  Text,
  Card,
  Loader,
  Divider,
  Button,
  Title,
  ScrollArea,
  Grid,
  Container,
} from "@mantine/core";
import { useParams } from "react-router-dom";
import {
  fetchTrips,
  fetchUnassignedStops,
  fetchUnassignedTrips,
  updateVehicleStop,
} from "../store/slices/tripsSlice";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import {
  TripResponse,
  TripStopResponse,
  VehicleStopPurpose,
  VehicleStopResponse,
  VehicleStopType,
  VehicleTripResponse,
} from "types/trip.types";
import Map from "components/Map/Map";
import DndTimelineChart, {
  DndTimeLineChartItem,
  DndTimelineChartRow,
} from "components/DndTimelineChart/DndTimelineChart";
import dayjs from "dayjs";
import { TripInfoPanel } from "components/trips/TripInfoPanel";
import EventRegistry, { EventHandler } from "utils/eventRegistry";
import { isAdmin } from "utils/user.utils";
import IdentifyStopPanel from "components/IdentifyStopPanel/IdentifyStopPanel";
import { CustomerResponse } from "types/responses.type";
import TripsService from "services/trips.service";

// Placeholder for Google Map and Timeline Chart

const Trips = () => {
  const { tripId } = useParams<{ tripId: string }>();
  const [date, setDate] = useState(dayjs().toDate());
  const dispatch = useDispatch<AppDispatch>();
  const [panelOpened, setPanelOpened] = useState(true);
  const [timelineOpened, setTimelineOpened] = useState(true);
  const [currentTripId, setCurrentTripId] = useState<number>();
  const [displayTripDetails, setDisplayTripDetails] = useState(false);
  const [currentUnassignedTripId, setCurrentUnassignedTripId] =
    useState<number>();
  const [vehicleStopToIdentify, setVehicleStopToIdentify] =
    useState<VehicleStopResponse>();
  const [eventRegistry, setEventRegistry] = useState<EventRegistry>();

  const loading = useSelector((state: RootState) => state.trips.loading);
  const user = useSelector((state: RootState) => state.auth.user);
  const adminMode = isAdmin(user);
  const trips = useSelector((state: RootState) => state.trips.trips);
  const unassignedTrips = useSelector(
    (state: RootState) => state.trips.unassignedTrips
  );
  const unassignedTripStop = useSelector(
    (state: RootState) => state.trips.unassignedStops
  );
  const [allTrips, setAllTrips] = useState<VehicleTripResponse[]>([]);

  useEffect(() => {
    dispatch(fetchTrips({ date, search: tripId }));
    dispatch(fetchUnassignedTrips({ date }));
    dispatch(fetchUnassignedStops({ date }));
  }, [date]);

  useEffect(() => {
    const aTrips = [...(trips ?? [])];
    const unassignedFakeVehicleTrip = {
      vehicleTripId: -1,
      vehicleStops: [],
      trips: [] as TripResponse[],
    };
    if (currentUnassignedTripId) {
      const unassignedTrip = unassignedTrips?.find(
        (t) => t.tripId === currentUnassignedTripId
      );
      if (unassignedTrip) {
        unassignedFakeVehicleTrip.trips.push(unassignedTrip);
      } else {
        unassignedFakeVehicleTrip.trips.push(...unassignedTrips);
      }
    } else {
      unassignedFakeVehicleTrip.trips.push(...unassignedTrips);
    }
    if (unassignedFakeVehicleTrip.trips.length)
      aTrips.push(unassignedFakeVehicleTrip as any);

    setAllTrips(aTrips);
  }, [trips, currentUnassignedTripId, unassignedTrips]);

  function getTripStopsOfCurrentTrip(): TripStopResponse[] {
    const tripStops: TripStopResponse[] = [];
    if (currentTripId) {
      const currentTrip = allTrips.find(
        (t) => t.vehicleTripId === currentTripId
      );
      if (currentTrip) {
        currentTrip.trips?.forEach((trip) => {
          trip.tripStops.forEach((tripStop) => {
            tripStops.push(tripStop);
          });
        });
      }
    }
    console.log(tripStops);

    return tripStops;
  }
  function focusOnStop(params: any) {
    setTimeout(() => {
      handleActions("focus-on-vehicle-stop", params);
    }, 500);
  }
  function handleActions(action: string, params?: any) {
    switch (action) {
      case "get-trip-stops":
        return getTripStopsOfCurrentTrip();
      case "date-change":
        setDate(params.date);
        break;
      case "set-trip-id":
        if (currentTripId == params.tripId) {
          setCurrentTripId(undefined);
        } else {
          setCurrentTripId(params.tripId);
        }
        break;
      case "set-tripId-to-display-details":
        if (params?.tripId) {
          setCurrentTripId(params.tripId);
          setDisplayTripDetails(true);
        } else {
          setDisplayTripDetails(false);
        }
        break;
      case "set-unassigned-tripId":
        if (currentUnassignedTripId == params.tripId) {
          setCurrentUnassignedTripId(undefined);
        } else {
          setCurrentUnassignedTripId(params.tripId);
        }
        break;
      case "map-stop-click":
      case "map-place-click":
        focusOnStop(params);
        setCurrentTripId(params.tripId);
        setDisplayTripDetails(Boolean(params.tripId));
        break;

      case "focus-on-vehicle-stop":
        eventRegistry?.raiseEvent("focusOnVehicleStop", params);
        break;
      case "identify-vehicle-stop":
        setVehicleStopToIdentify(params.stop);
        break;
      case "identify-stop-type":
        if (params?.stop?.vehicleStopId) {
          TripsService.updateVehicleStopTypeAndPurpose(
            params.stop.vehicleStopId,
            {
              purpose: params.purpose,
              type: params.type,
            }
          )
            .then((data) => {
              dispatch(
                updateVehicleStop({
                  updatedStop: data.data,
                  vehicleStopId: params?.stop?.vehicleStopId,
                })
              );
            })
            .catch((reason) => {
              console.error(reason);
            });
        }
        setVehicleStopToIdentify(undefined);
        focusOnStop({
          point: { ...params.stop },
          zoom: 18,
        });
        break;
      case "vehicle-stop-remove-purpose":
        if (params?.stop?.vehicleStopId) {
          TripsService.updateVehicleStopTypeAndPurpose(
            params?.stop?.vehicleStopId,
            {
              purpose: null,
              type: VehicleStopType.UNRELATED,
            }
          )
            .then((data) => {
              dispatch(
                updateVehicleStop({
                  updatedStop: data.data,
                  vehicleStopId: params?.stop?.vehicleStopId,
                })
              );
            })
            .catch((reason) => {
              console.error(reason);
            });
        }
        break;
      default:
        console.log(`Unknown action: ${action}`, params);
    }
  }
  useEffect(() => {
    if (!eventRegistry) setEventRegistry(new EventRegistry());
  }, []);
  return (
    <Group
      align="flex-start"
      m={0}
      gap={0}
      style={{
        height: "100%",
        flexGrow: 1,
        flexWrap: "nowrap",
        position: "relative",
      }}
    >
      {/* Left Collapsible Panel */}
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "row",
          height: "100%",
        }}
      >
        <div
          style={{
            width: panelOpened ? 300 : 0,
            overflow: "hidden",
            height: "100%",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            overflow: "hidden",
            top: 0,
            left: 0,
            bottom: 0,
            right: 0,
          }}
        >
          {vehicleStopToIdentify ? (
            <IdentifyStopPanel
              stop={vehicleStopToIdentify}
              location={vehicleStopToIdentify}
              onAction={handleActions}
              onClose={() => {
                const point = { ...vehicleStopToIdentify };
                setTimeout(() => {
                  handleActions("focus-on-vehicle-stop", { point });
                }, 500);
                handleActions("identify-vehicle-stop", { stop: undefined });
              }}
            />
          ) : (
            <TripInfoPanel
              vehiclerips={trips}
              unassignedTrips={unassignedTrips}
              currentUnassignedTripId={currentUnassignedTripId}
              currentTripId={currentTripId}
              onAction={handleActions}
              eventRegistry={eventRegistry}
              date={date}
              adminMode={adminMode}
              displayDetails={displayTripDetails}
              loading={loading}
            />
          )}
        </div>
        <div style={{ alignContent: "center", zIndex: 2, maxHeight: "100%" }}>
          {/* Narrow toggle button on right edge */}
          <Button
            variant="light"
            size="xs"
            style={{
              width: 24,
              height: 48,
              position: "absolute",
              padding: 0,
              top: "50%",
              marginTop: -24,
              borderRadius: "0 6px 6px 0",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
            }}
            onClick={() => setPanelOpened((v) => !v)}
          >
            {panelOpened ? "<" : ">"}
          </Button>
        </div>
      </div>

      {/* Main Content: Map */}
      <Stack style={{ flex: 1, height: "100%", padding: 0 }}>
        <div
          style={{
            height: "100%",
            width: "100%",
            background: "#222",
            borderRadius: 8,
          }}
        >
          <Map
            vehicleTrips={allTrips}
            currentTripId={currentTripId}
            eventRegistry={eventRegistry}
            onAction={handleActions}
            unassignedStops={unassignedTripStop}
          />
        </div>
      </Stack>
    </Group>
  );
};

export default Trips;
